import { useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Box,
  DataTableToolbar,
  DataTableToolbarActions,
  DataTableToolbarLeft,
} from "@salestrak/ui";

import { useEntityDetail } from "#app/components/entity";
import { TeamMembersRoster } from "#app/components/teams/team-members-roster";
import { LoadErrorState } from "#app/components/settings/load-error-state";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";

/**
 * `/team/$teamId/members` — the team's full roster as a view tab. Everyone
 * who can see the team sees the list (the route layout already object-guards
 * team visibility); management affordances (role menu, promote/remove row
 * actions) appear for `Team.viewerCanManage` holders — the server-computed
 * gate (workspace `manage.teams` OR a manager of this team), so the UI can
 * never show an affordance the mutations reject. Add member is NOT this
 * view's: the team layout seats it on the masthead for every tab, so this
 * route's band carries the quick filters and the search.
 */
function TeamMembersTabRoute() {
  const { teamId } = Route.useParams();
  const { teamMap } = useWorkspaceData();
  // `viewerCanManage` rides the workspace team map (batched on boot), so the
  // check costs no extra query here.
  const canManage = teamMap.get(teamId)?.viewerCanManage === true;

  const { openEntity } = useEntityDetail();

  const handleViewProfile = useCallback(
    (userId: string) => openEntity("user", userId, { via: "team_members" }),
    [openEntity],
  );

  return (
    <TeamMembersRoster
      teamId={teamId}
      canManage={canManage}
      onViewProfile={handleViewProfile}
      gutterTop="md"
    >
      {({ search, quickFilters, table, loading, error, retry }) =>
        !loading && error ? (
          <LoadErrorState noun="team" error={error} onRetry={retry} />
        ) : (
          <Box direction="column" fill="flex" minHeight={0}>
            {/* The view's toolbar band below the tab strip: the quick-filter
                chips lead (the members directory's set, minus Team — this IS
                one team), the search right-aligned. Column show/hide lives on
                the table's own circle-plus header. */}
            <DataTableToolbar>
              <DataTableToolbarLeft>{quickFilters}</DataTableToolbarLeft>
              <DataTableToolbarActions>{search}</DataTableToolbarActions>
            </DataTableToolbar>
            {/* No page scroller: the roster's table is its own scroll box
                (the bulk bar sits on its bottom edge). */}
            {table}
          </Box>
        )
      }
    </TeamMembersRoster>
  );
}

export const Route = createFileRoute("/(app)/$workspace/team/$teamId/members")({
  component: TeamMembersTabRoute,
});
